var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState } from "react";
import { Box, Button, Grid, TextField, useMediaQuery, useTheme, alpha, CircularProgress, } from "@mui/material";
import { PageHeader } from "@ui/components/header/PageHeader";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import usePermissionStyles from "./components/usePermissionStyles";
import StyledPermissionTable from "./components/StyledPermissionTable";
import StyledPermissionCards from "./components/StyledPermissionCards";
import { FInput } from "@ui/components/inputs";
import { useGetAllRolesQuery, useCreateRoleMutation, useUpdateRoleMutation, useGetRolePermissionsQuery, } from "../../../../features/projects/projectsApi";
import { useSnackbar } from "notistack";
import { usePermissions } from "../../../../context/PermissionContext";
import { stack1_5 } from "@ui/style/style";
import { WidgetLoader } from "@ui/components/loader/WidgetLoader";
import { NoModuleAccess } from "@ui/components/no-module-access/NoModuleAccess";
const PERMISSIONS = ["read", "update", "create", "delete"];
const RolePermissionsDetail = () => {
    const theme = useTheme();
    const styles = usePermissionStyles();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { roleId } = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const { canCreate, canUpdate, isLoading: permissionsLoading, } = usePermissions();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isEditMode = !!roleId && roleId !== "new";
    const [permissions, setPermissions] = useState({});
    // API hooks
    const { data: roles } = useGetAllRolesQuery();
    const [createRole, { isLoading: isCreating }] = useCreateRoleMutation();
    const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation();
    // Fetch role permissions when in edit mode
    const { data: rolePermissionsData } = useGetRolePermissionsQuery(roleId || "", { skip: !isEditMode });
    // Get selected role data in edit mode
    const selectedRole = React.useMemo(() => {
        if (isEditMode && roles) {
            return roles.find((r) => r.id === roleId);
        }
        return null;
    }, [isEditMode, roles, roleId]);
    // Form hook - must be declared before useEffect
    const { control, handleSubmit, reset, watch } = useForm({
        defaultValues: {
            role: "",
        },
    });
    // Watch the role field value from form
    const roleNameFromForm = watch("role");
    // Get the role name to display (prioritize selectedRole, then form value)
    const displayRoleName = React.useMemo(() => {
        // In edit mode, use selectedRole name
        if (isEditMode && (selectedRole === null || selectedRole === void 0 ? void 0 : selectedRole.name)) {
            return selectedRole.name;
        }
        // Otherwise, use form value
        if (roleNameFromForm) {
            return roleNameFromForm;
        }
        return "";
    }, [isEditMode, selectedRole === null || selectedRole === void 0 ? void 0 : selectedRole.name, roleNameFromForm]);
    useEffect(() => {
        const defaultPermissions = {
            projects: { read: true, update: true, create: true, delete: true },
            users: { read: false, update: false, create: false, delete: false },
            rolesPermissions: {
                read: false,
                update: false,
                create: false,
                delete: false,
            },
            companyConfigurations: {
                read: false,
                update: false,
                create: false,
                delete: false,
            },
        };
        // Populate role name in edit mode
        if (isEditMode && selectedRole) {
            reset({ role: selectedRole.name });
            // Transform API permissions to component format
            if (rolePermissionsData && rolePermissionsData.length > 0) {
                const transformedPermissions = {};
                rolePermissionsData.forEach((perm) => {
                    const moduleName = perm.moduleName.toLowerCase();
                    const permissionType = perm.permissionType.toLowerCase();
                    if (!transformedPermissions[moduleName]) {
                        transformedPermissions[moduleName] = {
                            read: false,
                            update: false,
                            create: false,
                            delete: false,
                        };
                    }
                    transformedPermissions[moduleName][permissionType] = perm.isAllowed;
                });
                setPermissions(transformedPermissions);
            }
            else {
                setPermissions(defaultPermissions);
            }
        }
        else {
            reset({ role: "" });
            setPermissions(defaultPermissions);
        }
    }, [isEditMode, selectedRole, rolePermissionsData, reset]);
    const handlePermissionChange = (module, permission, value) => {
        setPermissions((prev) => {
            const modulePerms = Object.assign({}, (prev[module] || {}));
            const index = PERMISSIONS.indexOf(permission);
            if (value) {
                for (let i = 0; i <= index; i++) {
                    modulePerms[PERMISSIONS[i]] = true;
                }
            }
            else {
                modulePerms[permission] = false;
            }
            return Object.assign(Object.assign({}, prev), { [module]: modulePerms });
        });
    };
    const isPermissionDisabled = (module, permissionIndex) => {
        if (!permissions || !permissions[module])
            return false;
        const currentModule = permissions[module];
        // Find highest ON permission in this module
        let highestOnIndex = -1;
        for (let i = PERMISSIONS.length - 1; i >= 0; i--) {
            if (currentModule[PERMISSIONS[i]]) {
                highestOnIndex = i;
                break;
            }
        }
        // If a permission is lower than highest ON, disable it
        if (permissionIndex < highestOnIndex) {
            return true;
        }
        return false;
    };
    const handleSaveRoleAndPermissions = (data) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            if (!isEditMode) {
                // Check create permission
                if (!canCreate("RolesPermissions")) {
                    enqueueSnackbar("U heeft geen toegang om rollen aan te maken", {
                        variant: "error",
                    });
                    return;
                }
                // Create new role
                // Transform permissions object to flat array for API
                // Example: { moduleName: 'User', permissionType: 'Read', isAllowed: true }
                const permissionsArray = Object.entries(permissions).flatMap(([module, perms]) => Object.entries(perms).map(([permissionType, isAllowed]) => ({
                    moduleName: module.charAt(0).toUpperCase() + module.slice(1),
                    permissionType: permissionType.charAt(0).toUpperCase() +
                        permissionType.slice(1),
                    isAllowed: Boolean(isAllowed),
                })));
                yield createRole({
                    name: data.role,
                    permissions: permissionsArray,
                }).unwrap();
                enqueueSnackbar("Rol succesvol aangemaakt", {
                    variant: "success",
                });
                navigate("/roles-permissions");
            }
            else {
                // Check update permission
                if (!canUpdate("RolesPermissions")) {
                    enqueueSnackbar("U heeft geen toegang om rollen bij te werken", {
                        variant: "error",
                    });
                    return;
                }
                // Update existing role
                // Transform permissions object to flat array for API
                const permissionsArray = Object.entries(permissions).flatMap(([module, perms]) => Object.entries(perms).map(([permissionType, isAllowed]) => ({
                    moduleName: module.charAt(0).toUpperCase() + module.slice(1),
                    permissionType: permissionType.charAt(0).toUpperCase() +
                        permissionType.slice(1),
                    isAllowed: Boolean(isAllowed),
                })));
                yield updateRole({
                    roleId: roleId,
                    body: {
                        name: data.role,
                        isActive: (_a = selectedRole === null || selectedRole === void 0 ? void 0 : selectedRole.isActive) !== null && _a !== void 0 ? _a : true,
                        permissions: permissionsArray,
                    },
                }).unwrap();
                enqueueSnackbar("Rol succesvol bijgewerkt", {
                    variant: "success",
                });
                navigate("/roles-permissions");
            }
        }
        catch (_b) {
            enqueueSnackbar("Fout bij het opslaan van rol", {
                variant: "error",
            });
        }
    });
    // Check permissions - prevent unauthorized access via URL
    const hasAccess = isEditMode
        ? canUpdate("RolesPermissions")
        : canCreate("RolesPermissions");
    if (permissionsLoading) {
        return (React.createElement(Box, { sx: { height: 300 } },
            React.createElement(WidgetLoader, { isLoading: true })));
    }
    if (!hasAccess) {
        return (React.createElement(React.Fragment, null,
            React.createElement(PageHeader, { onBack: () => navigate("/roles-permissions"), title: isEditMode
                    ? t("modules:rolesPermissions.updateRole")
                    : t("modules:rolesPermissions.addRole") }),
            React.createElement(NoModuleAccess, { message: isEditMode
                    ? "U heeft geen toegang om rollen bij te werken"
                    : "U heeft geen toegang om rollen aan te maken" })));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(PageHeader, { onBack: () => window.history.back(), title: isEditMode
                ? t("modules:rolesPermissions.updateRole")
                : t("modules:rolesPermissions.addRole"), actions: React.createElement(Button, { variant: "contained", color: "tertiary", size: isSmallScreen ? "medium" : "large", onClick: handleSubmit(handleSaveRoleAndPermissions), disabled: isCreating || isUpdating, startIcon: isCreating || isUpdating ? (React.createElement(CircularProgress, { size: 16, color: "inherit" })) : null }, isCreating || isUpdating
                ? isEditMode
                    ? "Opslaan..."
                    : "Aanmaken..."
                : isEditMode
                    ? t("common:actions.saveChanges")
                    : t("common:actions.addNow")) }),
        React.createElement(Grid, { container: true, spacing: 1.5, sx: {
                my: 1,
            } },
            React.createElement(Grid, { size: { xs: 12, sm: 12 } }, isEditMode ? (React.createElement(TextField, { label: t("common:labels.role"), value: displayRoleName, fullWidth: true, variant: "outlined", size: "medium", disabled: true, sx: {
                    "& .MuiOutlinedInput-root": {
                        borderRadius: "40px",
                        height: "45px",
                        backgroundColor: theme.palette.background.paper,
                        "& fieldset": {
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                            borderWidth: "1px",
                        },
                        "&:hover fieldset": {
                            borderColor: alpha(theme.palette.primary.main, 0.5),
                        },
                        "&.Mui-disabled": {
                            backgroundColor: theme.palette.background.paper,
                            "& fieldset": {
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                            },
                        },
                    },
                    "& .MuiInputBase-input": {
                        padding: "12px 16px",
                    },
                }, slotProps: {
                    input: {
                        readOnly: true,
                    },
                } })) : (React.createElement(FInput, { name: "role", control: control, label: t("common:labels.role"), rules: {
                    required: t("validation:required", {
                        field: t("common:labels.role"),
                    }),
                } })))),
        React.createElement(Box, { sx: styles.permissionContainer }, isMobile ? (React.createElement(Box, { sx: Object.assign({}, stack1_5) },
            React.createElement(StyledPermissionCards, { rows: Object.keys(permissions !== null && permissions !== void 0 ? permissions : {}), rolePermissions: permissions !== null && permissions !== void 0 ? permissions : {}, onChange: handlePermissionChange, isDisabled: isPermissionDisabled, t: t, isAllDisabled: false }))) : (React.createElement(StyledPermissionTable, { rows: Object.keys(permissions !== null && permissions !== void 0 ? permissions : {}), rolePermissions: permissions !== null && permissions !== void 0 ? permissions : {}, onChange: handlePermissionChange, isDisabled: isPermissionDisabled, t: t, isAllDisabled: false })))));
};
export default RolePermissionsDetail;
